import classnames from 'classnames';
import React, { Fragment, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useRouter } from 'next/router';
import { ICONS } from 'Components/utils/icon/icons';
import { getKeyValue, t } from 'Src/i18n';
import { useTracking } from 'Tracking/tracking-api/useTracking';
import { BE_GA_DEFAULT_PAYLOAD } from 'Views/booking/constants';
import { Icon } from 'Components/frameworks/icon/Icon';
import {
  patchProposalHandler,
  saveCustomerProposalsOpportunityIdHandler,
} from 'Store/booking/proposals/proposals.action';
import { Modal } from 'Components/frameworks/modal/Modal';
import { Button } from 'Components/frameworks/button/Button';
import { COLORS } from 'Components/utils/color/colors';
import config from 'Config';
import { isFeatureFlipped } from 'Utils/feature-flipping/featureFlippingUtils';
import { useAppContext } from 'Context/ReactAppContext';
type Props = {
  proposal: Proposal;
  isOnComparator?: boolean;
  resort: any;
};

const getFilename = (resort: any, proposal: Proposal) =>
  `${resort?.name?.replace(/ /g, '_')}-${proposal.arrivalDate}_${proposal.departureDate}`;

const handleSubmit =
  (
    proposal: Proposal,
    appContext: AppContext,
    router: any,
    dispatch: ReduxDispatch,
    track: (arg0: any) => void,
  ) =>
  (event) => {
    event.preventDefault();
    event.stopPropagation();
    track({});
    const hasIdentifiedAttendees = proposal.households.some((household) =>
      household.attendees.some((attendee) => attendee.customerId),
    );

    if (appContext.isCxo && hasIdentifiedAttendees) {
      patchProposalHandler(proposal.id, appContext, true)(dispatch);
      saveCustomerProposalsOpportunityIdHandler(
        proposal.id,
        appContext,
        router.query?.cxo_primarycontact,
        router.query?.cxo_opportunity_guid,
      )(dispatch);
    }

    event.target.submit();
  };

const PdfActionsModal = (props) => {
  const { proposal, isOnComparator = false, onClose, resort } = props;
  const appContext = useAppContext();
  const router = useRouter();
  const dispatch = useDispatch();
  const noIdentifiedAttendee = proposal.households.every(({ attendees }) =>
    attendees.every(({ customerId }) => !customerId),
  );
  const track = useTracking({
    ...BE_GA_DEFAULT_PAYLOAD,
    actionGA: 'banking',
    labelGA: isOnComparator ? 'downloadPDF' : 'CTA_download',
  });

  function onClickSendEmail() {
    const pageTypeParam = 'pagetype=entityrecord';
    const entityTypeParam = 'etn=email';
    const extraQueryParams = `extraqs=cxo_proposalids%3D${proposal.id}`;
    window.open(
      `${config.get('dynamics.url')}&${pageTypeParam}&${entityTypeParam}&${extraQueryParams}`,
    );
    saveCustomerProposalsOpportunityIdHandler(
      proposal.id,
      appContext,
      router.query?.cxo_primarycontact,
      router.query?.cxo_opportunity_guid,
    )(dispatch);
  }

  const filename = getFilename(resort, proposal);
  const apiKey = config.get('api.seller.api_key');
  return (
    <Modal
      ariaLabel={t('seller.downloadPdf.shareProposals')}
      closeLabel={t('common.close')}
      onClose={onClose}
    >
      <div className="text-blue p-20 text-center text-24 font-bold">
        {t('seller.downloadPdf.shareProposals')}
      </div>
      <div className="mb-40 flex w-full justify-center px-40">
        <div>
          <div
            className={classnames({
              'mb-20': !noIdentifiedAttendee,
            })}
          >
            {t('seller.downloadPdf.chooseMethod')}
          </div>
          {noIdentifiedAttendee && (
            <div className="text-danger text-sm mb-20 flex items-center font-bold">
              <Icon
                className="mr-5 flex-shrink self-start"
                color={COLORS.DANGER}
                name={ICONS.WARNING}
                width="1.5em"
              />
              {t('seller.downloadPdf.missingPaxInfo')}
            </div>
          )}
          <div className="flex w-full justify-center">
            <Button
              colorBg={COLORS.BLUE}
              className="rounded-5 mr-20"
              isDisabled={noIdentifiedAttendee}
              onClick={onClickSendEmail}
              size="mediumLarge"
            >
              <span className="px-4 text-16">{t('seller.downloadPdf.sendEmail')}</span>
            </Button>
            <form
              action="/proposal-pdf"
              method="post"
              onSubmit={handleSubmit(proposal, appContext, router, dispatch, track)}
            >
              <input type="hidden" name="proposalId" value={proposal.id} />
              <input type="hidden" name="filename" value={filename} />
              <input type="hidden" name="x-api-key" value={apiKey} />
              <input type="hidden" name="type" value="attachment" />
              <input type="hidden" name="downloadType" value="attachment" />
              <Button
                colorBg={COLORS.TURQUOISE}
                className="rounded-5"
                type="submit"
                size="mediumLarge"
              >
                <span className="px-4 text-16">{t('seller.downloadPdf.downloadPDF')}</span>
              </Button>
            </form>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export const B2BProposalPdfDownloadButton = (props: Props) => {
  const { proposal, resort } = props;
  const [isOpen, setIsOpen] = useState(false);
  const appContext = useAppContext();
  const router = useRouter();
  const dispatch = useDispatch();
  const track = useTracking({
    ...BE_GA_DEFAULT_PAYLOAD,
    actionGA: 'banking',
    labelGA: 'CTA_download',
  });
  const shouldDisplay =
    ((appContext.isDirectCxo || appContext.isIndirectCxo) &&
      !!getKeyValue('seller.downloadPdf.displayCxo')) ||
    (appContext.isCmta && !!getKeyValue('seller.downloadPdf.displayCmta'));

  if (!shouldDisplay) {
    return null;
  }

  const filename = getFilename(resort, proposal);
  const apiKey = config.get('api.seller.api_key');
  const shouldShareProposalByEmail =
    isFeatureFlipped(appContext)('booking.shareProposalByEmail') &&
    appContext.isCxo &&
    appContext.isDirectCxo;

  function onClick(event) {
    if (shouldShareProposalByEmail) {
      event.preventDefault();
      event.stopPropagation();
      setIsOpen(true);
    } else {
      handleSubmit(proposal, appContext, router, dispatch, track)(event);
    }
  }

  return (
    <Fragment>
      <form action="/proposal-pdf" method="post" onSubmit={onClick}>
        <input type="hidden" name="proposalId" value={proposal.id} />
        <input type="hidden" name="filename" value={filename} />
        <input type="hidden" name="x-api-key" value={apiKey} />
        <input type="hidden" name="type" value="attachment" />
        <input type="hidden" name="downloadType" value="attachment" />
        <button
          data-testid="pdf-download-button"
          className={classnames(
            'reset-button mx-10 flex flex-col items-center text-12 font-bold text-turquoise',
          )}
          type="submit"
        >
          <Icon className={classnames('h-auto')} name={ICONS.DOWNLOAD} width="1.25rem" />
          <span className="sr-only md:not-sr-only">{t('seller.downloadPdf.label')}</span>
        </button>
      </form>
      {isOpen && <PdfActionsModal {...props} onClose={() => setIsOpen(false)} />}
    </Fragment>
  );
};
